import React, { ExoticComponent, ReactNode } from 'react';
// @todo: Can remove once we stop aliasing 'react' and 'react-dom' in webpack to React 18.
// eslint-disable-next-line import/no-unresolved
import { createRoot } from 'react-dom/client';
import bugsnagReact from '@bugsnag/plugin-react';
import isPrintMode from 'ca-environment/isPrintMode';
import { ThemeProvider } from '@kaizen/design-tokens';
import { themeManager } from 'ca-theme-manager';
import { I18nLoader } from 'ca-ui/i18n';

const registeredComponentTypes = new Map();
let ready = false;

function getRegisteredComponentType(typeName: string) {
  if (!registeredComponentTypes.has(typeName)) {
    throw new Error(`component class "${typeName}" not registered`);
  }
  return registeredComponentTypes.get(typeName);
}

let ErrorBoundary: ExoticComponent<{ children?: ReactNode | undefined }>;

function mountComponent(ComponentType: React.ComponentType, node: HTMLElement) {
  const props = $(node).data('react-props');
  let Wrapper: ExoticComponent<{ children?: ReactNode | undefined }> =
    React.Fragment;

  // Wrap the component with ErrorBoundary (with the exception of this being run locally)
  if (window.location.protocol !== 'file:') {
    if (!ErrorBoundary) {
      // Note: window.bugsnagClient is not available until the page has loaded.
      window.bugsnagClient.use(bugsnagReact, React);
      ErrorBoundary = window.bugsnagClient.getPlugin('react');
    }
    Wrapper = ErrorBoundary;
  }

  const element = (
    <ThemeProvider themeManager={themeManager}>
      <Wrapper>
        <I18nLoader>
          <ComponentType {...props} />
        </I18nLoader>
      </Wrapper>
    </ThemeProvider>
  );

  // wkhtmltopdf doesn't log errors, so output them to the PDF itself
  if (process.env.NODE_ENV !== 'production') {
    if (isPrintMode()) {
      try {
        const root = createRoot(node);
        root.render(element);
      } catch (err) {
        require('ca-devtools/displayPDFError').default(err);
      }
      return;
    }
  }

  const root = createRoot(node);
  root.render(element);
}

function mountComponents(onlyTypeName?: string) {
  const selector = onlyTypeName
    ? `[data-react-component="${onlyTypeName}"]`
    : '[data-react-component]';

  $(selector)
    .toArray()
    .map((node) => {
      const typeName = $(node).data('react-component');

      mountComponent(getRegisteredComponentType(typeName), node);
    });
}

export default function registerComponentType(
  typeName: string,
  ComponentType: React.ComponentType<any>,
  isHotReloadUpdate?: boolean
) {
  if (registeredComponentTypes.has(typeName) && !isHotReloadUpdate) {
    throw new Error(`component class "${typeName}" already registered`);
  }
  registeredComponentTypes.set(typeName, ComponentType);

  if (ready) mountComponents(typeName);
}

$(() => {
  ready = true;

  mountComponents();
});

$(window).on('react-automount', (e, typeName) => {
  mountComponents(typeName);
});
